import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const STEPS = [
  {
    n: "01",
    t: "Submit complaint",
    d: "Email complaints@fortiqa.co.ke, WhatsApp +254 700 000 000, or use the form below. Include your merchant phone number, the policy or claim number, and a short description.",
  },
  {
    n: "02",
    t: "Fortiqa acknowledges",
    d: "We send an acknowledgement within 24 hours, assign a case officer, and confirm what we already know about your situation.",
  },
  {
    n: "03",
    t: "Investigation and response",
    d: "A senior team member reviews the case end-to-end. You'll receive a substantive written response within 5 business days — most cases close in this window.",
  },
  {
    n: "04",
    t: "Escalation if unresolved",
    d: "If you're not satisfied with our final response, you can escalate to the Insurance Regulatory Authority (IRA Kenya). We'll share their contact details and your full case file.",
  },
];

export default function Complaints() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    reference: "",
    description: "",
  });

  usePageMeta({
    title: "Complaints — Fortiqa",
    description:
      "How to raise a complaint with Fortiqa. Response within 5 business days. Escalation route to IRA Kenya for unresolved issues.",
  });

  function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    fetch(`/api/public/leads/complaint`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        reference: form.reference || null,
        description: form.description,
      }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || `Request failed (${r.status})`);
        }
        setSubmitted(true);
      })
      .catch((err) =>
        setError(err.message || "Couldn't submit right now — please WhatsApp us instead."),
      )
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="complaints-page">
      <MarketingPageShell
        eyebrow="Legal · Complaints"
        title="Tell us when something's wrong."
        intro="Had a poor experience, an unfair claim outcome, or you're stuck? We want to hear it. Every complaint gets reviewed by a senior team member with a written response within 5 business days."
        testid="complaints"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 space-y-5">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="rounded-2xl bg-white border border-[#114232]/10 p-7 flex items-start gap-5"
            >
              <div className="font-mono text-2xl font-bold text-[#c15337] shrink-0 w-10">
                {s.n}
              </div>
              <div>
                <div className="font-heading text-lg font-bold text-[#114232] mb-1.5">{s.t}</div>
                <p className="text-sm text-[#5c6360] leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12">
          <div className="rounded-3xl bg-white border border-[#114232]/10 p-7 sm:p-10">
            <div className="font-heading text-xl sm:text-2xl font-bold text-[#114232] mb-2">
              Submit a complaint
            </div>
            <p className="text-sm text-[#5c6360] mb-6">
              All fields marked <span className="text-[#c15337]">*</span> are required.
            </p>
            {submitted ? (
              <div
                className="rounded-2xl bg-[#2d7a4a]/8 border border-[#2d7a4a]/30 p-6 flex items-start gap-3"
                data-testid="complaints-submitted"
              >
                <CheckCircle2 className="w-5 h-5 text-[#2d7a4a] shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <div className="font-heading font-bold text-[#114232] mb-1">Complaint received</div>
                  <p className="text-sm text-[#5c6360] leading-relaxed">
                    Thanks {form.name || "for reaching out"} — we'll acknowledge within 24 hours
                    and respond substantively within 5 business days.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="grid sm:grid-cols-2 gap-4"
                data-testid="complaints-form"
              >
                <Field label="Your name" required>
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    data-testid="complaints-input-name"
                  />
                </Field>
                <Field label="Phone (WhatsApp)" required>
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                    data-testid="complaints-input-phone"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    data-testid="complaints-input-email"
                  />
                </Field>
                <Field label="Policy or claim reference">
                  <input
                    type="text"
                    placeholder="e.g. FQC-8821"
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    className={inputCls}
                    data-testid="complaints-input-reference"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Describe what happened" required>
                    <textarea
                      required
                      rows={6}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={inputCls + " resize-none"}
                      data-testid="complaints-input-description"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-2">
                  {error && (
                    <div className="text-xs text-[#c15337]" data-testid="complaints-error">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="complaints-submit"
                    className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#c15337] hover:bg-[#a8472f] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    {submitting ? "Submitting…" : "Submit complaint"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="rounded-2xl bg-[#c15337]/8 border border-[#c15337]/20 p-7 flex items-start gap-4">
            <AlertCircle className="w-5 h-5 text-[#c15337] shrink-0 mt-0.5" />
            <div>
              <div className="font-heading text-base font-bold text-[#114232] mb-1">
                Urgent fraud in progress?
              </div>
              <p className="text-sm text-[#5c6360] leading-relaxed">
                If money is actively being lost right now, call your bank or Safaricom first to
                freeze the transaction — then WhatsApp us and we'll start the FlashClaim
                immediately.
              </p>
            </div>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}

const inputCls =
  "w-full px-4 py-2.5 rounded-xl border border-[#114232]/15 bg-[#f4f2eb]/40 text-sm text-[#1b1d1c] focus:outline-none focus:border-[#114232]/40 focus:bg-white transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5c6360] mb-1.5">
        {label} {required && <span className="text-[#c15337]">*</span>}
      </span>
      {children}
    </label>
  );
}
